import React from 'react';
import { Asset } from '../../services/assetAPI';
import {
  Server,
  Database,
  Cloud,
  Code,
  FileText,
  Lock,
  Unlock,
  HardDrive,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Edit2,
  ExternalLink
} from 'lucide-react';

interface AssetListProps {
  assets: Asset[];
  isLoading: boolean;
  onAssetClick: (asset: Asset) => void;
  onEditAsset: (asset: Asset) => void;
}

const AssetList: React.FC<AssetListProps> = ({
  assets,
  isLoading,
  onAssetClick,
  onEditAsset
}) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'infrastructure': return <Server size={16} className="text-blue-500" />;
      case 'application': return <Code size={16} className="text-purple-500" />;
      case 'service': return <Cloud size={16} className="text-cyan-500" />;
      case 'code_repository': return <Code size={16} className="text-gray-500" />;
      case 'data': return <Database size={16} className="text-green-500" />;
      default: return <FileText size={16} className="text-gray-500" />;
    }
  };

  const getClassificationBadge = (classification: string) => {
    switch (classification) {
      case 'critical':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            CRITICAL
          </span>
        );
      case 'important':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            IMPORTANT
          </span>
        );
      case 'low_risk':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            LOW RISK
          </span>
        );
      default:
        return null;
    }
  };

  const getReviewStatusBadge = (status: string) => {
    switch (status) {
      case 'current':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-green-600">
            <CheckCircle size={12} /> Current
          </span>
        );
      case 'due_soon':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
            <Clock size={12} /> Due Soon
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-red-600">
            <AlertTriangle size={12} /> Overdue
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Clock size={12} /> Pending
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Loading assets...</span>
        </div>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Server size={48} className="mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900">No assets found</h3>
        <p className="text-sm text-gray-500 mt-1">
          Create your first asset to start tracking your infrastructure.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Asset
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Classification
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Security
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Review Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Owner
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {assets.map((asset) => (
            <tr
              key={asset.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onAssetClick(asset)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-lg">
                    {getTypeIcon(asset.asset_type)}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{asset.asset_name}</div>
                    <div className="text-sm text-gray-500">{asset.location || 'No location'}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-600 capitalize">
                  {asset.asset_type.replace('_', ' ')}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getClassificationBadge(asset.classification)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1" title={asset.encryption_status ? 'Encrypted' : 'Not Encrypted'}>
                    {asset.encryption_status ? (
                      <Lock size={14} className="text-green-500" />
                    ) : (
                      <Unlock size={14} className="text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-1" title={asset.backup_status ? 'Backup Enabled' : 'No Backup'}>
                    {asset.backup_status ? (
                      <HardDrive size={14} className="text-green-500" />
                    ) : (
                      <HardDrive size={14} className="text-gray-300" />
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getReviewStatusBadge(asset.review_status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-600">{asset.owner_name || 'Unassigned'}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditAsset(asset);
                  }}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Edit Asset"
                >
                  <Edit2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AssetList;
